<template>
    <button class="web-authn-button" @click="login">
        <i class="fas fa-fingerprint"></i>
    </button>
</template>

<script>
import { mapActions } from 'vuex';
import base64url from 'base64url';

import webViewMixin from '@/plugins/mixin/web-view.mixin';

export default {
    name: 'LoginWebAuthn',
    mixins: [webViewMixin],
    data() {
        return {
            loading: false,
        }
    },
    props: {
        email: {
            type: String,
            required: false,
            default: null

        }
    },
    methods: {
        ...mapActions('deviceAccounts', ['addAccount']),
        
        /**
         * Paso 1: Obtener las opciones de autenticación del servidor
         * @returns {Promise<Object>} Opciones formateadas para navigator.credentials.get
         */
        async getAuthenticationOptions() {
            try {
                this.loading = true;
                const config = { auth: { username: this.email, password: '' } };
                const { data } = await this.axiosAccount.get('/api/biometric-auth/authentication/options', config);
                const options = data.options;

                // Formatear allowCredentials si existen
                const allowCredentials = options.allowCredentials ? options.allowCredentials.map(cred => {
                    return {
                        ...cred,
                        id: new Uint8Array(base64url.toBuffer(cred.id))
                    };
                }) : [];

                const credential_options = {
                    challenge: Uint8Array.from(options.challenge, c => c.charCodeAt(0)),
                    rpId: options.rpId,
                    userVerification: options.userVerification,
                    allowCredentials: allowCredentials,
                    extensions: { uvm: true, loc: false },
                    timeout: options.timeout
                };
                
                return credential_options;
            } catch (error) {
                throw error;
            } finally {
                this.loading = false;
            }
        },

        /**
         * Paso 2: Obtener la credencial del dispositivo usando WebAuthn
         * @param {Object} options - Opciones de autenticación obtenidas del servidor
         * @returns {Promise<PublicKeyCredential>} Credencial WebAuthn
         */
        async getCredential(options) {
            try {
                this.loading = true;
                
                const credential = await navigator.credentials.get({ 
                    publicKey: options 
                });
                
                return credential;
            } catch (error) {
                throw error;
            } finally {
                this.loading = false;
            }
        },

        /**
         * Paso 3: Enviar la credencial al servidor para hacer login
         * @param {PublicKeyCredential} credential - Credencial WebAuthn obtenida
         * @returns {Promise<Object>} Datos de respuesta del login
         */
        async loginWithCredential(credential) {
            try {
                this.loading = true;
                
                // Formatear la credencial
                const format_credential = this.getFormattedCredential(credential);
                const credential_data = {
                    id: base64url.encode(credential.rawId),
                    credential: format_credential,
                    type: credential.type
                };
                                
                // Crear token de autorización
                const signature_string = `${this.email}:${JSON.stringify(credential_data)}`;
                const token = Buffer.from(signature_string).toString('base64');
                const config = { 
                    headers: { 
                        'Authorization': `Basic ${token}` 
                    }
                };                
                // Hacer POST al endpoint de login
                const { data } = await this.axiosAccount.post(
                    '/api/login/webauthn', 
                    { "token": " "}, 
                    config
                );
                
                return data;
            } catch (error) {
                throw error;
            } finally {
                this.loading = false;
            }
        },

        /**
         * Método principal que orquesta el proceso completo de login
         */
        async login() {
            try {
                // Paso 1: Obtener opciones de autenticación
                const options = await this.getAuthenticationOptions();
                
                // Paso 2: Obtener credencial del dispositivo
                const credential = await this.getCredential(options);
                
                // Paso 3: Hacer login con la credencial
                const data = await this.loginWithCredential(credential);
                
                // Procesar respuesta y redirigir
                let { redirect_url } = data;
                const webview = this.$route.query.webview;
                if (webview) {
                    redirect_url = this.formatWebviewRedirectUrl(redirect_url);
                }
                
                this.addAccount({ email: data.email });
                this.$toasted.global.successMessage('Login successfully.');
                window.location.replace(redirect_url);
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Error starting session with Passkey. Please try again later.';
                this.$toasted.global.errorMessage(errorMessage);
            } finally {
                this.loading = false;
            }
        },

        /**
         * Formatea la credencial para enviarla al servidor
         * @param {PublicKeyCredential} credential - Credencial WebAuthn
         * @returns {Object} Credencial formateada
         */
        getFormattedCredential(credential) {
            return {
                authenticatorData: base64url.encode(credential.response.authenticatorData),
                signature: base64url.encode(credential.response.signature),
                clientDataJSON: base64url.encode(credential.response.clientDataJSON),
                userHandle: base64url.encode(credential.response.userHandle)   
            };
        }
    }
}
</script>

<style lang="scss">
.web-authn-button {
    align-items: center;
    background-color: transparent;
    border: 1px solid #dadce0;
    border-radius: 4px;
    color: $accounts-primary;
    display: inline-flex;
    height: 40px;
    justify-content: center;
    width: 40px;
}
</style>